import { Alert, Box } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import DistributorsFiltersAutocomplete from '@/components/features/DistributorsList/DistributorsFiltersAutocomplete';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { Distributor, useDistributors } from '@/hooks/useDistributors';
import { useTableNavigation } from '@/hooks/useTableNavigation';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';

const columnHelper = createColumnHelper<Distributor>();

function DistributorsList() {
  const { organizationId } = useOrganizationId();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollTopParam = searchParams.get('scrollTop');
  const locationState = location.state as { returnSearchParams?: string } | undefined;
  const returnSearchParams = locationState?.returnSearchParams;
  const { navigateToDetail, restoreScrollPosition } = useTableNavigation({
    containerRef: tableContainerRef,
  });

  // Get pagination state from URL (single source of truth)
  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  // Get filter state from URL (single source of truth)
  const filterString = searchParams.get('groups');
  const filterEmails = searchParams.get('emails');

  const groupNames = filterString
    ? filterString
        .split(',')
        .filter(Boolean)
        .map((distributorName) => ({ distributorName }))
    : null;

  const emails = filterEmails
    ? filterEmails
        .split(',')
        .filter(Boolean)
        .map((email) => ({ email }))
    : null;

  // Fetch data with current pagination from URL
  const { distributors, isLoading, error } = useDistributors(apiPage, pagination.pageSize, 'distributorName', groupNames, emails);

  const distributorsList = distributors?.items ?? [];

  // Define columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('systemId', {
        header: 'ID Distributor',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('distributorName', {
        header: 'Distributor Name',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('isActive', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          return status ? 'Active' : 'Inactive';
        },
      }),
    ],
    [],
  );

  // Restore URL params from navigation state when coming back from detail page
  useEffect(() => {
    if (returnSearchParams) {
      const restoredParams = new URLSearchParams(returnSearchParams);
      const currentGroups = searchParams.get('groups');
      const currentEmails = searchParams.get('emails');
      const restoredGroups = restoredParams.get('groups');
      const restoredEmails = restoredParams.get('emails');

      if ((restoredGroups && restoredGroups !== currentGroups) || (restoredEmails && restoredEmails !== currentEmails)) {
        setSearchParams(restoredParams, { replace: true });
      }
    }
  }, [returnSearchParams, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isLoading && distributorsList.length > 0) {
      restoreScrollPosition();
    }
  }, [isLoading, distributorsList.length, restoreScrollPosition, scrollTopParam, returnSearchParams]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ pageIndex: 0, pageSize: newPageSize });
    const params = new URLSearchParams(window.location.search);
    params.delete('scrollTop');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const handleRowClick = (distributor: Distributor) => {
    if (distributor.systemId) {
      navigateToDetail(`/distributors/manage/${distributor.systemId}`, {
        distributor,
      });
    }
  };

  const tableWithData = useBaseTable<Distributor>({
    tableData: distributorsList,
    columns,
    isRowSelectionEnabled: false,
    isColumnVisibilityEnabled: false,
    isColumnOrderEnabled: false,
    hasPagination: true,
    pagination,
    onPaginationChange: setPagination,
    paginationCount: distributors?.totalCount ?? 0,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
  });

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-distributors'
      >
        Error loading Distributors: {error.message}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
        <DistributorsFiltersAutocomplete organizationId={organizationId} />
      </Box>
      <BaseTable<Distributor>
        {...tableWithData}
        testId='distributors-table'
        ariaLabel='Distributors list'
        containerRef={tableContainerRef}
        onRowClick={(_, row) => handleRowClick(row.original)}
        loadingData={{
          loadingMessage: 'Loading Distributors…',
          isLoading: isLoading,
        }}
        noDataMessage='No Distributors found.'
      />
    </>
  );
}

export default DistributorsList;
